import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User as UserIcon, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { Input } from "@/components/ui/input";

export default function Login() {
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Bentornato!");
      } else {
        await register({ email, password, name });
        toast.success("Account creato");
      }
      navigate(next);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="font-heading text-2xl font-semibold tracking-tight" data-testid="login-brand-link">
            auto<span className="text-[#0055FF]">.</span>advisor
          </Link>
          <h1 className="mt-6 font-heading text-3xl font-semibold tracking-tight">
            {mode === "login" ? "Bentornato" : "Crea un account"}
          </h1>
          <p className="mt-2 text-sm text-[#666]">
            Salva le ricerche e rivedi le auto suggerite in qualsiasi momento.
          </p>
        </div>

        <button
          type="button"
          onClick={googleLogin}
          className="w-full h-12 rounded-full border border-[#EAEAEA] bg-white flex items-center justify-center gap-3 hover:border-[#111] transition"
          data-testid="google-login-button"
        >
          <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" />
          <span className="font-medium">Continua con Google</span>
        </button>

        <div className="my-6 flex items-center gap-3 text-xs text-[#666]">
          <div className="flex-1 h-px bg-[#EAEAEA]" /> oppure con email <div className="flex-1 h-px bg-[#EAEAEA]" />
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === "register" && (
            <div className="relative">
              <UserIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome"
                className="h-12 pl-11 rounded-2xl border-[#EAEAEA] bg-white"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <Input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="h-12 pl-11 rounded-2xl border-[#EAEAEA] bg-white"
              data-testid="auth-email-input"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <Input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (min 6)"
              minLength={6}
              className="h-12 pl-11 rounded-2xl border-[#EAEAEA] bg-white"
              data-testid="auth-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-[#111] text-white hover:bg-[#333] transition inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="auth-submit-button"
          >
            {mode === "login" ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            {busy ? "Attendere…" : mode === "login" ? "Accedi" : "Crea account"}
          </button>
        </form>

        {mode === "login" && (
          <p className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-[#666] hover:text-[#0055FF]" data-testid="forgot-password-link">
              Password dimenticata?
            </Link>
          </p>
        )}

        <p className="mt-6 text-center text-sm text-[#666]">
          {mode === "login" ? "Non hai un account? " : "Hai già un account? "}
          <button
            type="button"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-[#0055FF] font-medium hover:underline"
            data-testid="auth-toggle-mode"
          >
            {mode === "login" ? "Registrati" : "Accedi"}
          </button>
        </p>
        <p className="mt-4 text-center text-sm">
          <Link to="/" className="text-[#666] hover:text-[#111]" data-testid="login-back-home">← Torna alla home</Link>
        </p>
      </motion.div>
    </div>
  );
}
